import { useState, useEffect, useRef } from 'react';
import { useOnboarding } from '../OnboardingContext';
import { useTranslation } from 'react-i18next';
import { Input } from '@/components/ui/input';
import { FlagImg } from '@/components/ui/CountryFlag';
import { Globe, Plus, X, AlertCircle, Search, Shield, Check, ChevronRight } from 'lucide-react';
import { countriesSorted, countries, type Country } from '@/data/countries';
import { validateTIN, getTINPlaceholder, getTINHint } from '@/utils/tinValidation';
import { PEPDeclarationForm } from './PEPDeclarationForm';
import { useScrollToActivate } from '@/hooks/useScrollToActivate';

interface TaxCountry {
  code: string;
  name: string;
  flag: string;
  tin?: string;
}

type EditingCountryState = { index: number; query: string } | null;

interface TinError {
  code: string;
  error?: string;
  hint?: string;
}

export function IndividualDeclarationsStep() {
  const { t } = useTranslation();
  const { data, updateData, goToNextStep, setCustomBackHandler } = useOnboarding();
  const { canActivate } = useScrollToActivate();
  
  const [taxResidencies, setTaxResidencies] = useState<TaxCountry[]>(
    data.taxResidencies || []
  );
  const [isUSResident, setIsUSResident] = useState<boolean | null>(data.isUSResident ?? null);
  const [giinNumber, setGiinNumber] = useState(data.giinNumber || '');
  const [showCountryPicker, setShowCountryPicker] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [tinErrors, setTinErrors] = useState<TinError[]>([]);
  const [isDetecting, setIsDetecting] = useState(true);
  const [editingCountry, setEditingCountry] = useState<EditingCountryState>(null);
  
  // PEP state
  const [isPEP, setIsPEP] = useState<boolean | null>(() => 
    data.isPEP !== undefined ? data.isPEP : null
  );
  const [showPEPForm, setShowPEPForm] = useState(false);

  const searchInputRef = useRef<HTMLInputElement>(null);

  // Auto-detect country on mount
  useEffect(() => {
    const detectCountry = async () => {
      if (data.taxResidencies && data.taxResidencies.length > 0) {
        setTaxResidencies(data.taxResidencies);
        setIsDetecting(false);
        return;
      }
      
      // Use country of residence as default if available
      if (data.countryOfResidence) {
        const countryData = countries.find(c => c.code === data.countryOfResidence);
        if (countryData) {
          setTaxResidencies([{ 
            code: countryData.code, 
            name: countryData.name, 
            flag: countryData.flag 
          }]);
        }
        setIsDetecting(false);
        return;
      }
      
      try {
        const response = await fetch('https://ipapi.co/json/');
        const geoData = await response.json();
        
        if (geoData?.country_code) {
          const detectedCountry = countries.find(c => c.code === geoData.country_code);
          if (detectedCountry) {
            setTaxResidencies([{ 
              code: detectedCountry.code, 
              name: detectedCountry.name, 
              flag: detectedCountry.flag 
            }]);
          }
        }
      } catch (err) {
      } finally {
        setIsDetecting(false);
      }
    };

    detectCountry();
  }, []);

  // Set up custom back handler
  useEffect(() => {
    if (showPEPForm) {
      setCustomBackHandler(() => {
        setShowPEPForm(false);
        return true;
      });
    } else if (showCountryPicker) {
      setCustomBackHandler(() => {
        setShowCountryPicker(false);
        setSearchQuery('');
        return true;
      });
    } else {
      setCustomBackHandler(null);
    }
    
    return () => setCustomBackHandler(null);
  }, [showPEPForm, showCountryPicker, setCustomBackHandler]);

  // Focus search input when picker opens
  useEffect(() => {
    if (showCountryPicker && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [showCountryPicker]);

  const filteredCountries = countriesSorted.filter(
    (c) =>
      c.name.toLowerCase().includes(searchQuery.toLowerCase()) &&
      !taxResidencies.some((tc) => tc.code === c.code)
  );

  const addCountry = (country: Country) => {
    const newResidency: TaxCountry = { 
      code: country.code, 
      name: country.name, 
      flag: country.flag 
    };
    const updated = [...taxResidencies, newResidency];
    setTaxResidencies(updated);
    updateData({ taxResidencies: updated });
    
    if (country.code === 'US') {
      setIsUSResident(true);
      updateData({ isUSResident: true });
    }
    
    setShowCountryPicker(false);
    setSearchQuery('');
  };

  const removeCountry = (code: string) => {
    const updated = taxResidencies.filter(c => c.code !== code);
    setTaxResidencies(updated);
    setTinErrors(prev => prev.filter(err => err.code !== code));
    updateData({ taxResidencies: updated });
    
    if (code === 'US') {
      setIsUSResident(null);
      setGiinNumber('');
      updateData({ isUSResident: false, giinNumber: '' });
    }
  };

  const swapCountry = (index: number, newCountry: Country) => {
    const old = taxResidencies[index];
    if (old.code === newCountry.code) {
      setEditingCountry(null);
      return;
    }
    // Prevent duplicates
    if (taxResidencies.some(tc => tc.code === newCountry.code)) {
      setEditingCountry(null);
      return;
    }
    const updated = taxResidencies.map((c, i) =>
      i === index ? { code: newCountry.code, name: newCountry.name, flag: newCountry.flag, tin: '' } : c
    );
    setTaxResidencies(updated);
    setTinErrors(prev => prev.filter(err => err.code !== old.code));
    updateData({ taxResidencies: updated });

    // Handle US residency flag
    if (old.code === 'US') {
      setIsUSResident(null);
      setGiinNumber('');
      updateData({ isUSResident: false, giinNumber: '' });
    }
    if (newCountry.code === 'US') {
      setIsUSResident(true);
      updateData({ isUSResident: true });
    }
    setEditingCountry(null);
  };

  const updateTIN = (code: string, tin: string) => {
    const updated = taxResidencies.map(c => 
      c.code === code ? { ...c, tin } : c
    );
    setTaxResidencies(updated);
    updateData({ taxResidencies: updated });

    if (tin.trim()) {
      const result = validateTIN(code, tin);
      if (!result.isValid) {
        setTinErrors(prev => [
          ...prev.filter(err => err.code !== code),
          { code, error: result.error, hint: result.hint }
        ]);
      } else {
        setTinErrors(prev => prev.filter(err => err.code !== code));
      }
    } else {
      setTinErrors(prev => prev.filter(err => err.code !== code));
    }
  };

  const handleUSResidentChange = (value: boolean) => {
    setIsUSResident(value);
    updateData({ isUSResident: value });
    if (!value) {
      setGiinNumber('');
      updateData({ giinNumber: '' });
    }
  };

  const handleGIINChange = (value: string) => {
    setGiinNumber(value);
    updateData({ giinNumber: value });
  };

  const handlePEPChange = (value: boolean) => {
    setIsPEP(value);
    if (!value) {
      updateData({ isPEP: false, pepDetails: '' });
    }
  };

  const hasUSResidency = taxResidencies.some(c => c.code === 'US') || isUSResident === true;
  
  const allTinsValid = taxResidencies.every(residency => {
    if (!residency.tin || residency.tin.trim() === '') return false;
    const result = validateTIN(residency.code, residency.tin);
    return result.isValid;
  });
  
  // Tax section valid
  const isTaxValid = taxResidencies.length > 0 && allTinsValid && 
    (hasUSResidency ? isUSResident !== null : true);
  
  // PEP section valid (must have made a selection)
  const isPEPValid = isPEP !== null && (isPEP === false || (isPEP === true && data.pepDetails));
  
  const isValid = isTaxValid && isPEPValid;

  const handleContinue = () => {
    if (!isTaxValid) return;
    
    // Set primary country of residence from first tax residency
    if (taxResidencies.length > 0) {
      updateData({ countryOfResidence: taxResidencies[0].code });
    }
    
    if (isPEP === true && !data.pepDetails) {
      // Show PEP form
      setShowPEPForm(true);
      return;
    }
    
    if (isPEP === false) {
      updateData({ isPEP: false, pepDetails: '' });
    }
    
    goToNextStep();
  };

  const handlePEPFormComplete = () => {
    setShowPEPForm(false);
    goToNextStep();
  };

  const handlePEPFormBack = () => {
    setShowPEPForm(false);
  };

  // Show PEP declaration form
  if (showPEPForm) {
    return (
      <PEPDeclarationForm 
        onBack={handlePEPFormBack}
        onComplete={handlePEPFormComplete}
      />
    );
  }

  if (isDetecting) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6 pt-24">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col pt-20 pb-28 px-4 sm:px-6">
      <div className="flex-1 flex flex-col max-w-md mx-auto w-full">
        <div className="animate-fade-in-up">
          <h1 className="text-xl sm:text-2xl font-bold text-center tracking-[0.15em] mb-1">
            {t('declarations.title', 'DECLARATIONS')}
          </h1>
          <p className="text-muted-foreground text-center text-sm mb-6">
            {t('declarations.subtitle', 'Tax residency and regulatory declarations')}
          </p>

          {/* Tax Residency Section */}
          <div className="rounded-xl border border-border bg-card p-4 mb-4">
            <div className="flex items-center gap-2 mb-3">
              <Globe className="w-4 h-4 text-primary" />
              <p className="font-medium text-sm">{t('taxResidency.title', 'Tax Residency')}</p>
            </div>
            
            <p className="text-xs text-muted-foreground mb-3">
              {t('taxResidency.subtitle', 'Provide the countries where you are tax resident with your TIN.')}
            </p>

            {/* Tax Residencies List */}
            {taxResidencies.length > 0 && (
              <div className="space-y-3 mb-3">
                {taxResidencies.map((country, index) => {
                  const tinError = tinErrors.find(err => err.code === country.code);
                  const tinHint = getTINHint(country.code);
                  const isEditingThis = editingCountry?.index === index;
                  const editQuery = editingCountry?.query || '';
                  const editFiltered = isEditingThis
                    ? countriesSorted.filter(
                        (c) =>
                          c.name.toLowerCase().includes(editQuery.toLowerCase()) &&
                          !taxResidencies.some((tc, ti) => ti !== index && tc.code === c.code)
                      )
                    : [];
                  return (
                    <div key={country.code} className="p-3 rounded-lg border border-border bg-muted/20">
                      <div className="flex items-center justify-between mb-2">
                        {isEditingThis ? (
                          <div className="flex-1 mr-2 relative">
                            <div className="relative">
                              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                              <input
                                type="text"
                                value={editQuery}
                                onChange={(e) => setEditingCountry({ index, query: e.target.value })}
                                placeholder="Search country..."
                                className="w-full pl-8 pr-8 py-1.5 text-sm rounded-lg bg-muted/30 border border-border focus:outline-none focus:ring-2 focus:ring-primary/50"
                                autoFocus
                              />
                              <button
                                onClick={() => setEditingCountry(null)}
                                className="absolute right-2 top-1/2 -translate-y-1/2"
                              >
                                <X className="w-3.5 h-3.5 text-muted-foreground" />
                              </button>
                            </div>
                            {editFiltered.length > 0 && (
                              <div className="absolute z-10 mt-1 w-full max-h-32 overflow-y-auto border border-border rounded-lg bg-card shadow-lg">
                                {editFiltered.slice(0, 8).map((c) => (
                                  <button
                                    key={c.code}
                                    onClick={() => swapCountry(index, c)}
                                    className="w-full flex items-center gap-2 px-3 py-2 hover:bg-muted/50 text-left text-sm border-b border-border last:border-b-0"
                                  >
                                    <FlagImg code={c.code} size="sm" />
                                    <span>{c.name}</span>
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        ) : (
                          <button
                            onClick={() => setEditingCountry({ index, query: '' })}
                            className="flex items-center gap-2 hover:opacity-70 transition-opacity"
                          >
                            <FlagImg code={country.code} size="sm" />
                            <span className="font-medium text-sm">{country.name}</span>
                            <ChevronRight className="w-3.5 h-3.5 text-muted-foreground -rotate-90" />
                          </button>
                        )}
                        {taxResidencies.length > 1 && !isEditingThis && (
                          <button 
                            onClick={() => removeCountry(country.code)} 
                            className="p-1.5 hover:bg-muted rounded-lg transition-colors"
                          >
                            <X className="w-3.5 h-3.5 text-muted-foreground" />
                          </button>
                        )}
                      </div>
                      <div className="space-y-1">
                        {tinHint && (
                          <p className="text-xs text-muted-foreground">
                            {t('taxResidency.format', 'Format')}: {tinHint}
                          </p>
                        )}
                        <Input
                          value={country.tin || ''}
                          onChange={(e) => updateTIN(country.code, e.target.value)}
                          placeholder={getTINPlaceholder(country.code)}
                          className={`h-9 text-sm ${tinError ? 'border-destructive' : 'border-border'}`}
                        />
                        {tinError && (
                          <div className="text-xs text-destructive">
                            <p>{tinError.error}</p>
                            {tinError.hint && (
                              <p className="text-muted-foreground mt-0.5">{tinError.hint}</p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Add Country */}
            {showCountryPicker ? (
              <div className="space-y-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder={t('taxResidency.searchCountry', 'Search country...')}
                    className="w-full pl-9 pr-8 py-2 text-sm rounded-lg bg-muted/30 border border-border focus:outline-none focus:ring-2 focus:ring-primary/50"
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery('')}
                      className="absolute right-3 top-1/2 -translate-y-1/2"
                    >
                      <X className="w-4 h-4 text-muted-foreground" />
                    </button>
                  )}
                </div>
                <div className="max-h-32 overflow-y-auto border border-border rounded-lg">
                  {filteredCountries.slice(0, 10).map((country) => (
                    <button
                      key={country.code}
                      onClick={() => addCountry(country)}
                      className="w-full flex items-center gap-2 px-3 py-2 hover:bg-muted/50 text-left text-sm border-b border-border last:border-b-0"
                    >
                      <FlagImg code={country.code} size="sm" />
                      <span>{country.name}</span>
                    </button>
                  ))}
                  {filteredCountries.length === 0 && (
                    <div className="text-center py-4 text-muted-foreground text-xs">
                      {t('taxResidency.noCountries', 'No countries found')}
                    </div>
                  )}
                </div>
                <button
                  onClick={() => { setShowCountryPicker(false); setSearchQuery(''); }}
                  className="w-full p-2 text-sm text-muted-foreground hover:text-foreground"
                >
                  {t('common.cancel', 'Cancel')}
                </button>
              </div>
            ) : (
              <button
                onClick={() => setShowCountryPicker(true)}
                className="w-full p-2.5 rounded-lg border-2 border-dashed border-border hover:border-primary/50 transition-colors flex items-center justify-center gap-2 text-muted-foreground hover:text-foreground text-sm"
              >
                <Plus className="w-4 h-4" />
                <span>{t('taxResidency.addCountry', 'Add tax residency')}</span>
              </button>
            )}

            {/* US Person Question */}
            {taxResidencies.length > 0 && !taxResidencies.some(c => c.code === 'US') && (
              <div className="mt-4 pt-3 border-t border-border/50">
                <p className="font-medium text-sm mb-2">
                  {t('taxResidency.usPersonQuestion', 'Are you a US person for tax purposes?')}
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleUSResidentChange(false)}
                    className={`flex-1 p-2 rounded-lg border-2 transition-all text-sm ${
                      isUSResident === false
                        ? 'border-primary bg-primary/10'
                        : 'border-border bg-muted/20 hover:border-muted-foreground/30'
                    }`}
                  >
                    {t('common.no', 'No')}
                  </button>
                  <button
                    onClick={() => handleUSResidentChange(true)}
                    className={`flex-1 p-2 rounded-lg border-2 transition-all text-sm ${
                      isUSResident === true
                        ? 'border-primary bg-primary/10'
                        : 'border-border bg-muted/20 hover:border-muted-foreground/30'
                    }`}
                  >
                    {t('common.yes', 'Yes')}
                  </button>
                </div>
              </div>
            )}

            {/* FATCA Section */}
            {hasUSResidency && (
              <div className="mt-3 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30">
                <div className="flex items-start gap-2 mb-2">
                  <AlertCircle className="w-4 h-4 text-amber-500 mt-0.5" />
                  <div>
                    <p className="font-medium text-sm">{t('taxResidency.fatcaTitle', 'FATCA Requirement')}</p>
                    <p className="text-xs text-muted-foreground">{t('taxResidency.giinHint', 'GIIN number if applicable')}</p>
                  </div>
                </div>
                <Input
                  value={giinNumber}
                  onChange={(e) => handleGIINChange(e.target.value)}
                  placeholder={t('taxResidency.giinPlaceholder', 'GIIN (optional)')}
                  className="h-9 text-sm"
                />
              </div>
            )}
          </div>

          {/* PEP Section */}
          <div className="rounded-xl border border-border bg-card p-4">
            <div className="flex items-center gap-2 mb-3">
              <Shield className="w-4 h-4 text-primary" />
              <p className="font-medium text-sm">{t('pep.title', 'PEP Declaration')}</p>
            </div>

            <p className="text-xs text-muted-foreground mb-3">
              {t('pep.question', 'Are you, or any of your close family members or associates, a Politically Exposed Person?')}
            </p>

            <div className="space-y-2">
              <button
                onClick={() => handlePEPChange(false)}
                className={`w-full p-3 rounded-lg border-2 transition-all flex items-center justify-between ${
                  isPEP === false
                    ? 'border-primary bg-primary/10'
                    : 'border-border bg-muted/20 hover:border-muted-foreground/30'
                }`}
              >
                <span className="text-sm font-medium">{t('pep.notPep', 'No, I am not a PEP')}</span>
                {isPEP === false && <Check className="w-4 h-4 text-primary" />}
              </button>

              <button
                onClick={() => handlePEPChange(true)}
                className={`w-full p-3 rounded-lg border-2 transition-all flex items-center justify-between ${
                  isPEP === true
                    ? 'border-primary bg-primary/10'
                    : 'border-border bg-muted/20 hover:border-muted-foreground/30'
                }`}
              >
                <div className="text-left">
                  <span className="text-sm font-medium">{t('pep.isPep', 'Yes, I am a PEP')}</span>
                  <p className="text-xs text-muted-foreground mt-0.5">{t('pep.formRequired', 'Complete LMAML declaration')}</p>
                </div>
                {isPEP === true ? <Check className="w-4 h-4 text-primary" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Fixed Bottom Button */}
      <div className="fixed bottom-0 left-0 right-0 p-4 bg-background border-t border-border safe-area-inset-bottom">
        <div className="max-w-md mx-auto">
          <button
            onClick={handleContinue}
            disabled={!isTaxValid || isPEP === null || !canActivate}
            className="w-full h-14 text-base font-semibold rounded-full bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          >
            {t('common.continue', 'Continue')}
          </button>
        </div>
      </div>
    </div>
  );
}
